import { Column } from "primereact/column";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { ManagedEntity, OutletContext } from "~/Pages/Tenants/types/tenants";
import { getName } from "country-list";
import ReactCountryFlag from "react-country-flag";
import Menu from "~/components/menu/Menu";

const ManagedEntitiesTable = () => {
  const [data, setData] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<1 | -1 | undefined>(1);
  const [loading, setLoading] = useState(false);
  const skeletonRows = [...Array(5)];
  const navigate = useNavigate();
  const actionMenuItems = (rowData: ManagedEntity) => [
    {
      label: "View Entity",
      icon: "pi pi-eye",
      command: () => navigate(`/entity/${rowData.id}/dashboard`),
    },
  ];

  const { fetchManagedEntities, managerId } = useOutletContext<OutletContext>();
  useEffect(() => {
    fetchData();
  }, [first, rows, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const page = first / rows + 1;
      const response = await fetchManagedEntities({
        account_manager_id: managerId,
        page: page.toString(),
        pageSize: rows.toString(),
        sortBy: sortBy ?? "",
        sortOrder: sortOrder === 1 ? "asc" : "desc",
      });
      setData(response.data.entities);
      setTotalRecords(response.data.pagination.total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onPage = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  };
  const onSort = (event: DataTableSortEvent) => {
    setSortBy(event.sortField);
    const order =
      event.sortOrder === 1 || event.sortOrder === -1
        ? event.sortOrder
        : undefined;
    setSortOrder(order);
  };

  const countryTemplate = (rowData: ManagedEntity) => {
    const countryName = rowData.country ? getName(rowData.country) : "";
    const countryFlag = rowData.country && (
      <ReactCountryFlag countryCode={rowData.country} svg />
    );
    return (
      <span className="flex gap-1">
        {countryFlag}
        {countryName}
      </span>
    );
  };
  return (
    <div className="table-container">
      <DataTable
        lazy
        value={loading ? skeletonRows : data}
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={[10, 20, 30]}
        paginator={totalRecords > 10 && !loading}
        sortField={sortBy}
        sortOrder={sortOrder}
        onSort={onSort}
        className="datatable-right-align"
        emptyMessage="No records found"
      >
        <Column
          field="name"
          header="Registered Name"
          className="table-col-width"
          sortable
          sortField="name"
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <Link
                to={`/entity/${rowData.id}/dashboard`}
                className="officer__link"
              >
                {rowData.name}
              </Link>
            )
          }
        />
        <Column
          field="type"
          header="Type"
          sortable
          className="table-col-width"
          body={(rowData) =>
            loading ? <Skeleton width="100%" /> : rowData.type
          }
        />
        <Column
          field="country"
          header="Country"
          sortable
          className="table-col-width"
          body={(rowData) =>
            loading ? <Skeleton width="100%" /> : countryTemplate(rowData)
          }
        />
        <Column
          field="parent"
          header="Parent Entity"
          sortable
          className="table-col-width"
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <Link
                to={`/entity/${rowData?.parentEntity?.id}/dashboard`}
                className="officer__link"
              >
                {rowData?.parentEntity?.name}
              </Link>
            )
          }
        />

        <Column
          className="table-col-width"
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <div className="flex align-items-center justify-content-end  gap-0 ">
                <Menu menuItems={actionMenuItems(rowData)} />
              </div>
            )
          }
        />
      </DataTable>
    </div>
  );
};

export default ManagedEntitiesTable;
